/**
 * useSpotlight Hook (Phase 19E: UI Highlighting & Guided Tours)
 *
 * Manages spotlight/guided tour state for highlighting UI elements.
 * Used by SpotlightOverlay component to show step-by-step highlights.
 *
 * Features:
 * - Cross-page navigation between steps (calls onNavigate when step.page differs)
 * - waitForElement() polling replaces fragile setTimeout delays (Rule 15)
 * - Centralized timing constants from spotlight.constants.ts
 * - useEffect-driven navigation prevents stale closure issues
 *
 * @module UseSpotlight
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SpotlightStep } from '../types/chatbot.types';
import { SPOTLIGHT_DELAYS } from '../constants/spotlight.constants';

// ============================================================================
// Utility: waitForElement
// ============================================================================

/**
 * Poll for a DOM element by data-spotlight-id.
 * Replaces fragile setTimeout delays — Rule 15: no magic numbers.
 *
 * @returns Promise that resolves with the element, or null if timeout.
 */
export function waitForElement(
  spotlightId: string,
  maxWait = SPOTLIGHT_DELAYS.ELEMENT_POLL_MAX_WAIT,
  interval = SPOTLIGHT_DELAYS.ELEMENT_POLL_INTERVAL,
  abortSignal?: AbortSignal
): Promise<Element | null> {
  return new Promise((resolve) => {
    if (abortSignal?.aborted) {
      resolve(null);
      return;
    }

    const selector = `[data-spotlight-id="${spotlightId}"]`;
    // Check immediately
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const startTime = Date.now();
    const timer = setInterval(() => {
      if (abortSignal?.aborted) {
        clearInterval(timer);
        resolve(null);
        return;
      }
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(timer);
        resolve(el);
      } else if (Date.now() - startTime >= maxWait) {
        clearInterval(timer);
        resolve(null);
      }
    }, interval);

    // Cleanup on abort
    abortSignal?.addEventListener(
      'abort',
      () => {
        clearInterval(timer);
        resolve(null);
      },
      { once: true }
    );
  });
}

// ============================================================================
// Types
// ============================================================================

interface SpotlightState {
  /** Whether spotlight is active */
  isActive: boolean;
  /** Current step index */
  currentStepIndex: number;
  /** All steps */
  steps: SpotlightStep[];
  /** Target element bounding rect (null if element not found) */
  targetRect: DOMRect | null;
  /** Whether cross-page navigation is in progress */
  isNavigating: boolean;
}

export interface UseSpotlightOptions {
  /** Callback to navigate to a different page (e.g., router.push) */
  onNavigate?: (path: string) => void;
  /** Get current path (without locale prefix) */
  getCurrentPath?: () => string;
}

export interface UseSpotlightReturn {
  /** Whether spotlight is active */
  isActive: boolean;
  /** Current step (null if inactive) */
  currentStep: SpotlightStep | null;
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Target element bounding rect */
  targetRect: DOMRect | null;
  /** Whether cross-page navigation is in progress */
  isNavigating: boolean;
  /** Start spotlight with given steps */
  startSpotlight: (steps: SpotlightStep[]) => void;
  /** Stop/close spotlight */
  stopSpotlight: () => void;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing spotlight/guided tour state.
 *
 * Navigation is driven by useEffect reacting to step index changes,
 * which prevents stale closure issues with fire-and-forget async calls.
 *
 * @example
 * const { isActive, currentStep, startSpotlight, stopSpotlight, nextStep } = useSpotlight({
 *   onNavigate: (path) => router.push(path),
 *   getCurrentPath: () => window.location.pathname.replace(/^\/(?:en|tr)/, ''),
 * });
 *
 * // Start a tour
 * startSpotlight([
 *   { spotlightId: 'export-btn', title: 'Export', description: 'Click to export data' },
 * ]);
 */
export function useSpotlight(options: UseSpotlightOptions = {}): UseSpotlightReturn {
  const { onNavigate, getCurrentPath } = options;

  const [state, setState] = useState<SpotlightState>({
    isActive: false,
    currentStepIndex: 0,
    steps: [],
    targetRect: null,
    isNavigating: false,
  });

  // Refs to always access latest callback values (avoids stale closures in useEffect)
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const getCurrentPathRef = useRef(getCurrentPath);
  getCurrentPathRef.current = getCurrentPath;

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  /**
   * Measure target element and update state.
   * Uses waitForElement polling instead of setTimeout.
   */
  const measureElement = useCallback(
    async (spotlightId: string, abortSignal?: AbortSignal) => {
      const el = await waitForElement(spotlightId, undefined, undefined, abortSignal);
      if (abortSignal?.aborted) return;

      if (el) {
        const rect = el.getBoundingClientRect();
        setState((prev) => ({ ...prev, targetRect: rect }));

        // Scroll element into view if off-screen
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Re-measure after scroll animation
          await new Promise((r) => setTimeout(r, SPOTLIGHT_DELAYS.SCROLL_COMPLETION));
          if (abortSignal?.aborted) return;
          const updatedRect = el.getBoundingClientRect();
          setState((prev) => ({ ...prev, targetRect: updatedRect }));
        }
      } else {
        setState((prev) => ({ ...prev, targetRect: null }));
      }
    },
    []
  );

  // ========================================================================
  // Core effect: navigate and measure when step changes
  // ========================================================================
  useEffect(() => {
    if (!state.isActive || state.steps.length === 0) return;
    const step = state.steps[state.currentStepIndex];
    if (!step) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    (async () => {
      const nav = onNavigateRef.current;
      const getPath = getCurrentPathRef.current;

      // Narration-only step: no element to highlight (intentional center-screen
      // tooltip). Empty spotlightId means "do not poll/measure" — avoids a
      // full waitForElement timeout against a selector that can never match.
      const isNarrationOnly = !step.spotlightId;

      // Check if cross-page navigation is needed
      const needsNavigation =
        step.page && nav && getPath && !getPath().startsWith(step.page);

      if (needsNavigation) {
        setState((prev) => ({ ...prev, isNavigating: true }));
        nav(step.page!);
        // Wait for the new page to render. For narration-only steps there is no
        // target element to poll for, so just let navigation settle briefly.
        if (isNarrationOnly) {
          await new Promise((r) => setTimeout(r, SPOTLIGHT_DELAYS.SCROLL_COMPLETION));
        } else {
          await waitForElement(step.spotlightId, undefined, undefined, signal);
        }
        if (signal.aborted) return;
      }

      if (isNarrationOnly) {
        // Center-screen narration: clear any stale target rect.
        setState((prev) => ({ ...prev, targetRect: null, isNavigating: false }));
        return;
      }

      await measureElement(step.spotlightId, signal);
      if (signal.aborted) return;

      setState((prev) => ({ ...prev, isNavigating: false }));
    })();

    return () => {
      abortController.abort();
    };
    // state.steps is stable (only changes on startSpotlight/stopSpotlight)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isActive, state.currentStepIndex, measureElement]);

  // ========================================================================
  // State-only actions (no side effects — navigation driven by effect above)
  // ========================================================================

  /**
   * Start a spotlight sequence.
   */
  const startSpotlight = useCallback((steps: SpotlightStep[]) => {
    if (steps.length === 0) return;
    setState({
      isActive: true,
      currentStepIndex: 0,
      steps,
      targetRect: null,
      isNavigating: false,
    });
  }, []);

  /**
   * Stop the spotlight.
   */
  const stopSpotlight = useCallback(() => {
    setState({
      isActive: false,
      currentStepIndex: 0,
      steps: [],
      targetRect: null,
      isNavigating: false,
    });
  }, []);

  /**
   * Advance to next step.
   */
  const nextStep = useCallback(() => {
    setState((prev) => {
      if (!prev.isActive) return prev;
      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex >= prev.steps.length) {
        return { isActive: false, currentStepIndex: 0, steps: [], targetRect: null, isNavigating: false };
      }
      return { ...prev, currentStepIndex: nextIndex, targetRect: null };
    });
  }, []);

  /**
   * Go to previous step.
   */
  const prevStep = useCallback(() => {
    setState((prev) => {
      if (!prev.isActive || prev.currentStepIndex <= 0) return prev;
      return { ...prev, currentStepIndex: prev.currentStepIndex - 1, targetRect: null };
    });
  }, []);

  // Keyboard listener: ESC to close, left/right arrows for navigation
  useEffect(() => {
    if (!state.isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        stopSpotlight();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isActive, stopSpotlight, nextStep, prevStep]);

  // Update target rect on window resize
  useEffect(() => {
    if (!state.isActive) return;

    const handleResize = () => {
      const step = state.steps[state.currentStepIndex];
      if (step) {
        measureElement(step.spotlightId);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [state.isActive, state.currentStepIndex, state.steps, measureElement]);

  // Cleanup observers on unmount
  useEffect(() => {
    const resizeObserver = resizeObserverRef.current;
    const mutationObserver = mutationObserverRef.current;
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  const currentStep = state.isActive ? (state.steps[state.currentStepIndex] ?? null) : null;

  return {
    isActive: state.isActive,
    currentStep,
    currentStepIndex: state.currentStepIndex,
    totalSteps: state.steps.length,
    targetRect: state.targetRect,
    isNavigating: state.isNavigating,
    startSpotlight,
    stopSpotlight,
    nextStep,
    prevStep,
  };
}
